import { useEffect, useRef, useCallback } from 'react';

function revealElement(el: Element) {
  el.classList.add('visible');
  el.querySelectorAll('.bar-fill').forEach((bar) => {
    setTimeout(() => bar.classList.add('animated'), 100);
  });
  el.querySelectorAll('.gap-fill').forEach((fill) => {
    setTimeout(() => fill.classList.add('animated'), 200);
  });
}

function isElementInViewport(el: HTMLElement, threshold: number): boolean {
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
  if (visibleHeight <= 0) return false;
  const elementHeight = rect.height;
  return elementHeight > 0 && (visibleHeight / elementHeight) >= threshold;
}

function observeWithFallback(
  observer: IntersectionObserver,
  el: HTMLElement,
  threshold: number,
) {
  observer.observe(el);
  requestAnimationFrame(() => {
    if (isElementInViewport(el, threshold)) {
      revealElement(el);
      observer.unobserve(el);
    }
  });
}

export function useScrollReveal(threshold = 0.1) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementsRef = useRef<Set<HTMLElement>>(new Set());
  const thresholdRef = useRef(threshold);
  thresholdRef.current = threshold;

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            revealElement(entry.target);
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );

    elementsRef.current.forEach((el) => {
      if (!el.classList.contains('visible')) {
        observeWithFallback(observerRef.current!, el, threshold);
      }
    });

    return () => {
      observerRef.current?.disconnect();
    };
  }, [threshold]);

  const observe = useCallback((el: HTMLElement | null) => {
    if (!el) {
      elementsRef.current.forEach((tracked) => {
        if (!tracked.isConnected) {
          elementsRef.current.delete(tracked);
          observerRef.current?.unobserve(tracked);
        }
      });
      return;
    }
    elementsRef.current.add(el);
    if (observerRef.current && !el.classList.contains('visible')) {
      observeWithFallback(observerRef.current, el, thresholdRef.current);
    }
  }, []);

  return observe;
}

export function useScrollProgress(elementId = 'v2-progress-bar') {
  useEffect(() => {
    const handleScroll = () => {
      const bar = document.getElementById(elementId);
      if (!bar) return;
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      bar.style.width = progress + '%';
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [elementId]);
}
